import { usePostHog } from "@posthog/react";
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { capture } from '../analytics/posthog';
import ApiClient, { HttpResponse } from '../config/ApiClient';
import { AssessmentData, Questions } from "../object-actions/types/types";

export interface AssessmentResponse {
    assessment_question_id: number;
    response: number;
    response_text?: string;
    author?: number; // Optional author ID for the API
}

interface AssessmentContextType {
    title: string;
    description: string;
    assessmentId: number | null;
    assignmentId: number | null;
    questions: Questions[];
    currentQuestionIndex: number;
    responses: AssessmentResponse[];
    isLoading: boolean;
    error: string | null;
    progress: number;
    totalQuestions: number;
    isComplete: boolean;
    answeredQuestions: number;
    /** 1-based submission ordinal from GET /api/assessments/:id (pre=1, post=2, …). */
    submissionsCount: number | null;
    /** Set from POST /api/assessments/complete/ `message` on success. */
    completionMessage: string | null;
    loadAssessment: (assessmentId: number) => Promise<void>;
    submitResponse: (assessmentQuestionId: number, response: number, responseText?: string) => Promise<boolean>;
    goToNextQuestion: () => void;
    goToPreviousQuestion: () => void;
    goToQuestion: (index: number) => void;
    submitAssessment: () => Promise<boolean>;
    resetAssessment: () => void;
    retryLastAction: () => Promise<void>;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export const useAssessment = () => {
    const context = useContext(AssessmentContext);
    if (!context) {
        throw new Error('useAssessment must be used within an AssessmentProvider');
    }
    return context;
};

interface AssessmentProviderProps {
    children: ReactNode;
}

export const AssessmentProvider: React.FC<AssessmentProviderProps> = ({ children }) => {
    const posthog = usePostHog();
    const [questions, setQuestions] = useState<Questions[]>([]);
    const [title, setTitle] = useState<string>("Generic Health Assesssment")
    const [description, setDescription] = useState<string>("")
    const [assessmentId, setAssessmentId] = useState<number | null>(null);
    const [assignmentId, setAssignmentId] = useState<number | null>(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [responses, setResponses] = useState<AssessmentResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastAction, setLastAction] = useState<(() => Promise<any>) | null>(null);
    const [submissionsCount, setSubmissionsCount] = useState<number | null>(null);
    const [completionMessage, setCompletionMessage] = useState<string | null>(null);

    const totalQuestions = questions.length;
    const answeredQuestions = responses.length;
    const isComplete = totalQuestions > 0 && answeredQuestions === totalQuestions;
    const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

    const loadAssessment = async (assessmentId: number) => {
        if (isLoading === true) return;
        setIsLoading(true);
        setError(null);
        setCompletionMessage(null);
        setLastAction(() => () => loadAssessment(assessmentId));

        try {
            const response: HttpResponse<AssessmentData> = await ApiClient.get(`/api/assessments/${assessmentId}`);

            if (response.success && response.data) {
                // Extract questions from the assessment
                const assessmentQuestions = response.data.questions || [];
                setTitle(response.data.title);
                setDescription(response.data.description || "");
                setAssessmentId(assessmentId);
                // Extract assignment_id from response (may be undefined if no assignment found)
                setAssignmentId((response.data as any).assignment_id || null);
                const sn = (response.data as AssessmentData).submissions_count;
                setSubmissionsCount(typeof sn === 'number' ? sn : null);
                setQuestions(assessmentQuestions);

                // Restore existing responses from the API
                const existingResponses: AssessmentResponse[] = [];
                assessmentQuestions.forEach((question: any) => {
                    if (question.response !== undefined && question.response !== null) {
                        existingResponses.push({
                            assessment_question_id: question.assessment_question_id,
                            response: Number(question.response),
                        });
                    }
                });

                setResponses(existingResponses);

                // Find the first unanswered question or start from the beginning
                // Check if response property exists and has a valid value
                const firstUnansweredIndex = assessmentQuestions.findIndex(
                    (q: any) => !('response' in q) || q.response === undefined || q.response === null
                );
                const targetIndex = firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0;
                setCurrentQuestionIndex(targetIndex);
            } else {
                const waiverMsg =
                    response.errors?.waiver_required
                        ? 'You must sign the waiver before taking the assessment.'
                        : null;
                throw new Error(waiverMsg || response.error || 'Failed to load assessment');
            }
        } catch (err: any) {
            let errorMessage = 'Failed to load assessment';

            if (err.response?.status === 403) {
                errorMessage = 'You do not have access to this assessment. Please check your program assignments.';
            } else if (err.response?.status === 401) {
                errorMessage = 'Please log in to access this assessment.';
            } else if (err.response?.status === 404) {
                errorMessage = 'Assessment not found.';
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            console.error('Error loading assessment:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const submitResponse = async (assessmentQuestionId: number, response: number, responseText?: string): Promise<boolean> => {
        if (!assessmentId) {
            setError('Assessment ID is required to submit responses');
            return false;
        }

        if (!assignmentId) {
            setError('Assignment ID is required to submit responses');
            return false;
        }

        try {
            // Persist immediately to backend per submission
            const payload: any = {
                assessment_question: assessmentQuestionId,
                assessment: assessmentId,
                assignment_id: assignmentId
            };
            // EITHER response OR response_text is required; we prefer numeric response here
            if (typeof response === 'number') {
                payload.response = response;
            } else if (responseText) {
                payload.response_text = responseText;
            }
            const result = await ApiClient.post('/api/question-responses/', payload);
            if (!result.success) {
                throw new Error(result.error || 'Failed to submit response');
            }

            capture(posthog, "question_response", {
                assessment_id: assessmentId,
                assignment_id: assignmentId,
                assessment_question_id: assessmentQuestionId,
                response: response,
                response_text: responseText,
                ...(submissionsCount != null ? { submissions_count: submissionsCount } : {}),
            });

            // Mirror locally
            setResponses(prev => {
                const existingIndex = prev.findIndex(r => r.assessment_question_id === assessmentQuestionId);
                if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex] = { assessment_question_id: assessmentQuestionId, response };
                    return updated;
                } else {
                    return [...prev, { assessment_question_id: assessmentQuestionId, response }];
                }
            });

            // Clear any previous errors on successful submission
            setError(null);
            return true;
        } catch (err: any) {
            let errorMessage = 'Failed to submit response';

            if (err.response?.status === 400) {
                errorMessage = 'Invalid response. Please check your answer and try again.';
            } else if (err.response?.status === 401) {
                errorMessage = 'Please log in to submit responses.';
            } else if (err.response?.status === 403) {
                errorMessage = 'You do not have permission to submit responses for this assessment.';
            } else if (err.message) {
                errorMessage = err.message;
            }

            console.error('Error submitting response:', err);
            setError(errorMessage);
            return false;
        }
    };

    const goToNextQuestion = () => {
        if (currentQuestionIndex < totalQuestions - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const goToPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const goToQuestion = (index: number) => {
        if (index >= 0 && index < totalQuestions) {
            setCurrentQuestionIndex(index);
        }
    };

    const submitAssessment = async (): Promise<boolean> => {
        if (responses.length === 0) return false;
        if (!assessmentId) {
            setError('Assessment ID is required to complete assessment');
            return false;
        }

        if (!assignmentId) {
            setError('Assignment ID is required to complete assessment');
            return false;
        }

        capture(posthog, "assessment_completed", {
            assessment_id: assessmentId,
            assignment_id: assignmentId,
            ...(submissionsCount != null ? { submissions_count: submissionsCount } : {}),
        });

        setIsLoading(true);
        setError(null);
        setLastAction(() => () => submitAssessment());

        try {
            // Trigger agents and handle assessment completion
            const result = await ApiClient.post<{
                message?: string;
                agent_queue_created?: boolean;
            }>('/api/assessments/complete/', {
                assessment_id: assessmentId,
                assignment_id: assignmentId
            });

            if (!result.success) {
                throw new Error(result.error || 'Failed to complete assessment');
            }
            const msg = result.data?.message;
            if (typeof msg === 'string' && msg.length > 0) {
                setCompletionMessage(msg);
            }
            return true;
        } catch (err: any) {
            let errorMessage = 'Failed to complete assessment';

            if (err.response?.status === 400) {
                errorMessage = 'Assessment completion failed. Please check your responses and try again.';
            } else if (err.response?.status === 401) {
                errorMessage = 'Please log in to complete the assessment.';
            } else if (err.response?.status === 403) {
                errorMessage = 'You do not have permission to complete this assessment.';
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            console.error('Error completing assessment:', err);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const retryLastAction = async () => {
        if (lastAction) {
            await lastAction();
        }
    };

    const resetAssessment = () => {
        setQuestions([]);
        setTitle("Generic Health Assesssment");
        setDescription("");
        setAssessmentId(null);
        setAssignmentId(null);
        setSubmissionsCount(null);
        setCompletionMessage(null);
        setCurrentQuestionIndex(0);
        setResponses([]);
        setError(null);
    };

    const value: AssessmentContextType = {
        title,
        description,
        assessmentId,
        assignmentId,
        questions,
        currentQuestionIndex,
        responses,
        isLoading,
        error,
        progress,
        totalQuestions,
        isComplete,
        answeredQuestions,
        submissionsCount,
        completionMessage,
        loadAssessment,
        submitResponse,
        goToNextQuestion,
        goToPreviousQuestion,
        goToQuestion,
        submitAssessment,
        resetAssessment,
        retryLastAction,
    };

    return (
        <AssessmentContext.Provider value={value}>
            {children}
        </AssessmentContext.Provider>
    );
};
